import { createEntityAdapter } from '@reduxjs/toolkit';
import apiSlice from '..';
import { EntityWithCount } from '../types';
import { defaultSerializeQueryArgs } from '@reduxjs/toolkit/query';
import { convertToFormData } from 'utils/api';

export type Banner = {
  name: string;
  description: string;
  status: string;
  image_url: string;
  image_name: string;
  created_at: string;
  updated_at: string;
  aa_order?: number;
  fl_order?: number;
  sc_order?: number;
  auction_types: string[];
};

const bannerAdapter = createEntityAdapter<Banner>();

export const bannersApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getActiveBanners: builder.query<any, string>({
      query: auctionType => ({
        url: '/banners',
        params: { auctionType }
      }),
      transformResponse: (r: any) => r.data
    }),
    getAllBanners: builder.query<EntityWithCount<Banner>, any>({
      query: ({ limit, offset, ...filters }) => ({
        url: '/admin/banners',
        method: 'GET',
        params: { limit, offset, ...filters }
      }),
      providesTags: ['Banners'],
      transformResponse: (response: any) => {
        return {
          rows: bannerAdapter.addMany(bannerAdapter.getInitialState(), response.data.rows),
          count: Number(response.data.count)
        };
      },
      serializeQueryArgs: ({ endpointName, queryArgs, endpointDefinition }) => {
        const { offset, ...restArgs } = queryArgs;
        return defaultSerializeQueryArgs({ queryArgs: restArgs, endpointDefinition, endpointName });
      },
      merge: (currentCache, newItems, { arg }) => {
        const newBanners = newItems.rows.ids.map(id => newItems.rows.entities[id]) as Banner[];
        currentCache.count = newItems.count;
        if (arg.offset === 1) {
          bannerAdapter.setAll(currentCache.rows, newBanners);
        } else {
          bannerAdapter.addMany(currentCache.rows, newBanners);
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.offset !== previousArg?.offset;
      }
    }),
    createBanner: builder.mutation<void, any>({
      query: body => ({
        url: `admin/banners`,
        method: 'POST',
        body: convertToFormData(body),
        formData: true
      }),
      invalidatesTags: ['Banners']
    }),
    editBanner: builder.mutation<void, { body: any; id: number }>({
      query: ({ id, body }) => ({
        url: `admin/banners/${id}`,
        method: 'PATCH',
        body: convertToFormData(body),
        formData: true
      }),
      invalidatesTags: ['Banners']
    }),
    deleteBanner: builder.mutation<void, number>({
      query: id => ({
        url: `admin/banners/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Banners']
    })
  }),
  overrideExisting: true
});

export const {
  useGetActiveBannersQuery,
  useGetAllBannersQuery,
  useCreateBannerMutation,
  useEditBannerMutation,
  useDeleteBannerMutation
} = bannersApiSlice;
